import React, { useEffect, useState } from 'react';
import { X, User, Loader2, CheckCircle, AlertCircle, MapPin } from 'lucide-react';
import { Membership, updateProfile } from '@/lib/portal-api-client';

interface UpdateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  membershipId: string;
  membership: Membership;
  onUpdateComplete: () => void;
}

export function UpdateProfileModal({
  isOpen,
  onClose,
  membershipId,
  membership,
  onUpdateComplete,
}: UpdateProfileModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    setSuccess(false);
    setError('');

    setFirstName(membership.customer_first_name || '');
    setLastName(membership.customer_last_name || '');
    setEmail(membership.customer_email || '');
    setPhone(membership.customer_phone || '');
    setAddress(membership.customer_address || '');
    setCity(membership.customer_city || '');
    setState(membership.customer_state || '');
    setZip(membership.customer_zip || '');
  }, [isOpen, membership]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    setLoading(true);
    setError('');

    try {
      const nextFirst = firstName.trim();
      const nextLast = lastName.trim();
      const nextEmail = email.trim().toLowerCase();
      const nextPhone = phone.trim();
      const nextAddress = address.trim();
      const nextCity = city.trim();
      const nextState = state.trim().toUpperCase();
      const nextZip = zip.trim();

      if (!nextFirst || !nextLast) {
        throw new Error('Please enter your first and last name.');
      }
      if (!nextEmail || !nextEmail.includes('@')) {
        throw new Error('Please enter a valid email address.');
      }
      if (!nextPhone || nextPhone.replace(/\D/g, '').length < 10) {
        throw new Error('Please enter a valid phone number.');
      }
      if (!nextAddress || !nextCity || !nextState || !nextZip) {
        throw new Error('Please complete your service address.');
      }

      await updateProfile(membershipId, {
        customer_first_name: nextFirst,
        customer_last_name: nextLast,
        customer_email: nextEmail,
        customer_phone: nextPhone,
        customer_address: nextAddress,
        customer_city: nextCity,
        customer_state: nextState,
        customer_zip: nextZip,
      });

      setSuccess(true);
      setTimeout(() => {
        onUpdateComplete();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err?.message || 'Failed to update your profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Update Profile</h2>
              <p className="text-sm text-gray-600">Edit your contact information</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Success */}
        {success && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Profile Updated!</h3>
            <p className="text-gray-600">Your information has been saved.</p>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="First"
                  autoComplete="given-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Last"
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="you@email.com"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="(555) 555-5555"
                  autoComplete="tel"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Service Address
              </label>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Street address"
                autoComplete="street-address"
              />
              <div className="grid grid-cols-3 gap-3 mt-3">
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="City"
                  autoComplete="address-level2"
                />
                <input
                  value={state}
                  onChange={(e) => setState(e.target.value.toUpperCase())}
                  maxLength={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="ST"
                  autoComplete="address-level1"
                />
                <input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="ZIP"
                  autoComplete="postal-code"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-3 px-6 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <User className="w-5 h-5" />
                    Save Profile
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
